import * as React from 'react'
import { CastingStore, StoreProps, castingStoreCanRenderCastingTypeInput } from '../../../../services/stores/casting.store'
import { I18nService } from '../../../../services/utils/i18n.service'
import { TypedID } from '../../../../services/utils/iots.service'
import LoadingComponent from '../../../shared/loading.component'
import SelectableListComponent from '../../../shared/selectable-list.component'
import { Input, NoResult } from '../casting-styled.component'
import { useMediaQuery } from '../../../../hooks/use-media-query'
import { useTheme } from 'styled-components'

const i18nScoped = new I18nService(require('./casting-offered-service-tree.component.yml'))

export default function CastingOfferedServiceTreeComponent ({ store, data }: StoreProps) {
  const { offeredServices, offeredService, serviceCategory, serviceCategories, offeredServiceEnableSearch, offeredServiceSearchKeywords, forceRenderingNotVisibleServices, offeredServiceRequestedId, renderingMode } = data
  const theme = useTheme()
  const isSmallScreen = useMediaQuery(`(max-width: ${ theme.breakpoints.m }px)`)

  const isOfferedServiceVisible = React.useCallback((os: CastingStore['data']['offeredServices'][0]) => {
    return (forceRenderingNotVisibleServices || !os.not_visible || offeredServiceRequestedId === os.id)
  }, [forceRenderingNotVisibleServices, offeredServiceRequestedId])

  const doesCategoryContainsAnyOfferedService = React.useCallback((serviceCategory: CastingStore['data']['serviceCategory']) => {
    if (offeredServices.some(os => serviceCategory.offered_service_ids.includes(os.id) && isOfferedServiceVisible(os))) return true

    for (const child of serviceCategories) {
      if (child.parent_category_id === serviceCategory.id && doesCategoryContainsAnyOfferedService(child)) return true
    }

    return false
  }, [offeredServices, serviceCategories, isOfferedServiceVisible])

  const renderedCategories = React.useMemo(() => {
    return serviceCategories
      .filter(sc => sc.parent_category_id === (serviceCategory ? serviceCategory.id : null))
      .filter(sc => doesCategoryContainsAnyOfferedService(sc))
      .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
  }, [serviceCategories, serviceCategory, doesCategoryContainsAnyOfferedService])

  const renderedServices = React.useMemo(() => {
    return offeredServices
      .filter(os => {
        if (!isOfferedServiceVisible(os) && os.id !== offeredService?.id) return false
        if (serviceCategory) return serviceCategory.offered_service_ids.includes(os.id)
        return !serviceCategories.find(_ => _.offered_service_ids.includes(os.id))
      })
      .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
  }, [offeredServices, offeredService, serviceCategory, serviceCategories, isOfferedServiceVisible])

  const isOfferedService = React.useCallback((item: any): item is CastingStore['data']['offeredService'] => {
    return offeredServices.includes(item)
  }, [offeredServices])

  const selectItem = React.useCallback(async (item: any) => {
    if (!isOfferedService(item)) return store.set({ serviceCategory: item })

    await store.setOfferedService(item)

    if (!castingStoreCanRenderCastingTypeInput(store.data)) store.nextFocus({ closeOnMobile: !store.data.offeredService.enable_lodging_ical_link })
    else if (renderingMode !== 'widget') store.set({ focusedInput: null })
  }, [isOfferedService, store, renderingMode])

  const renderServiceCategoryPathForOfferedService = React.useCallback((offeredService: StoreProps['data']['offeredServices'][0]) => {
    const serviceCategoriesPath = [serviceCategories.find(sc => sc.offered_service_ids.includes(offeredService.id))]
    while (serviceCategoriesPath[0]) serviceCategoriesPath.unshift(serviceCategories.find(sc => sc.id === serviceCategoriesPath[0].parent_category_id))
    serviceCategoriesPath.shift()
    return serviceCategoriesPath.flatMap(sc => [
      <span key={ sc.id }>{ sc.name }</span>,
      <em key={ sc.id + '-separator' } className='icon-next u-mls u-mrs' style={{ marginTop: '-0.3em' }} />
    ])
  }, [serviceCategories])

  if (offeredServiceEnableSearch) {
    // allow the user to search a service or product with keywords
      const fullTextIndex = store.asyncComputedProperties.offeredServicesFullTextIndex.readOrAsyncCompute([offeredServices, serviceCategories])
      if (!fullTextIndex) return <LoadingComponent />

      const searchResults = fullTextIndex
        .search(offeredServiceSearchKeywords)
        .filter((result, _, array) => result.score > array[0].score * 0.25)
        .map(result => offeredServices.find(os => os.id === +result.ref))
        .filter(result => isOfferedServiceVisible(result))

    return <React.Fragment>
      <div style={{ position: 'relative' }}>
        <Input
          className='u-mbm'
          placeholder={ i18nScoped.t('keywords_in_name_or_description') }
          value={ offeredServiceSearchKeywords }
          onChange={ evt => store.set({ offeredServiceSearchKeywords: evt.target.value }) }
          autoFocus />

        <a
          className="icon-close"
          style={{ position: 'absolute', right: 0, top: 0, padding: '0.8em' }}
          title={ i18nScoped.t('disable_search_with_keyword') }
          onClick={ () => store.set({ offeredServiceEnableSearch: false, offeredServiceSearchKeywords: '' }) } />
      </div>

      { (() => {
        if (offeredServiceSearchKeywords.length < 5) return <NoResult>{ i18nScoped.t('please_enter_at_least_5_characters') }</NoResult>
        if (searchResults.length === 0) return <NoResult>{ i18nScoped.t('no_services') }</NoResult>

        return <SelectableListComponent
          name="castingFilteredOfferedService"
          formatter={ os => <><div style={{ fontSize: '80%', fontWeight: 'bold' }}>{ renderServiceCategoryPathForOfferedService(os) }</div> { os.name }</> }
          data={ searchResults }
          value={ searchResults.find(os => os.id === offeredService?.id) }
          onChange={ item => selectItem(item) } />
      })() }
    </React.Fragment>
  } else {
    // renders services and categories as a tree
    type Mixed = { id: TypedID<'ServiceCategory' | 'OfferedService'>, name?: string, content?: string }
    const mixed = (renderedCategories as Mixed[]).concat(renderedServices)
    mixed.sort((a, b) => (a.name || a.content).localeCompare(b.name || b.content))
    if (mixed.length === 0) return <NoResult>{ i18nScoped.t('no_services') }</NoResult>

    return <SelectableListComponent
      name="castingServiceAndCaterogy"
      cssIdGenerator={ item => `js-test-${isOfferedService(item) ? 'service' : 'category'}-${item.id}` }
      formatter={ item => {
        if (!isOfferedService(item)) {
          return <div style={{ display: 'flex', gap: '1.25em', justifyContent: 'space-between', alignItems: 'center' }}>
            { isSmallScreen && <span /> /* allows centering the category (as we do for services) on small screens */ }
            <span>{ item.name || item.content }</span>
            <em className="icon-next" />
          </div>
        }
        return item.name
      } }
      data={ mixed }
      value={ mixed.find(item => isOfferedService(item) ? item.id === offeredService?.id : item.id === serviceCategory?.id) }
      onChange={ item => selectItem(item) } />
  }
}
